import { casesFilterSchema } from "@/components/Forms/schema/caseFormSchema.ts";
import BulkReassignmentModal from "@/components/Table/BulkAssignment";
import AdvancedFiltersModal from "@/components/Table/Filters/AdvancedFiltersModal.tsx";
import ExportModal, { type AvailableField } from "@/components/Table/Filters/ExportModal";
import FilterBadge from "@/components/Table/Filters/FilterBadge.tsx";
import SearchBar from "@/components/Table/Filters/SearchBar.tsx";
import caseColumnDefs from "@/components/Table/MrtCaseColumnLists/CaseColumnDefs.tsx";
import SearchableToolbar, { SEARCH_FIELDS } from "@/components/Table/SearchableToolbar.tsx";
import { useEmployee } from "@/contexts/EmployeeProvider.tsx";
import { getSolReportMrtStyle } from "@/functions/getSolReportMrtStyle.ts";
import { useDeleteCase } from "@/mutations/useDeleteCase.tsx";
import { useExportCases } from "@/mutations/useExportCases.tsx";
import { useSaveCaseFilter } from "@/mutations/useSaveCaseFilter.tsx";
import {
    type CaseFilter,
    type CaseFilterEditing,
    useFetchCaseFiltersets,
} from "@/queries/useFetchCaseFiltersets.ts";
import { useFetchMrtCases } from "@/queries/useFetchMrtCases.ts";
import type { MrtDashboardCase } from "@/queries/useFetchMrtDashboardCases.ts";
import { getErrorMessage } from "@/utils/api.ts";
import { zodResolver } from "@hookform/resolvers/zod";
import DeleteIcon from "@mui/icons-material/Delete";
import { Box, IconButton, Paper, Stack, Typography } from "@mui/material";
import {
    type MRT_ColumnDef,
    type MRT_ColumnFiltersState,
    type MRT_PaginationState,
    type MRT_Row,
    type MRT_SortingState,
    MaterialReactTable,
    useMaterialReactTable,
} from "material-react-table";
import { enqueueSnackbar } from "notistack";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import type { z } from "zod";
import CaseStatusesForCasesTable from "../Forms/MrtCaseTableFilters/CaseStatusesForCasesTable";
import CaseTypesForCasesTable from "../Forms/MrtCaseTableFilters/CaseTypesForCasesTable";
import DaysToSolForCasesTable from "../Forms/MrtCaseTableFilters/DaysToSolForCasesTable";

export type CasesTableFilter = z.infer<typeof casesFilterSchema>;

export type CaseApiResponse = {
    data: Case[];
    meta: {
        totalRowCount: number;
    };
};

export type Case = {
    person: {
        namePrefix?: string;
        nameFirst?: string;
        nameI?: string;
        nameLast?: string;
        nameAKA?: string;
        nameSuffix?: string;
        crmStatus?: string;
        race?: string;
        sex?: string;
        ssnumber?: string;
        taxIDNumber?: string;
        dlNumber?: string;
        dlissuedBy?: string;
        dateofbirth?: string;
        dateofdeath?: string;
        idReferredBy?: string;
        idSourceView?: string;
    };
    id: string;
    idUUID: string | null;
    clientName: string | null;
    caseType: string | null;
    caseStatus: string | null;
    caseStatusUM: string | null;
    caseName: string | null;
    dateOfIncident: Date | null;
    dateOfIncidentAge: number | null;
    lastTouch: string | null;
    assignedTeam: string | null;

    accidentCaseNumber: string;
    accidentReportDone: string;
    accidentSummary: string;
    ambulanceName: string;
};

const MrtCaseTable = () => {
    const navigate = useNavigate();
    const { hasPermission } = useEmployee();
    const deleteCase = useDeleteCase();
    const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>([]);
    const [globalFilter, setGlobalFilter] = useState("");
    const [sorting, setSorting] = useState<MRT_SortingState>([]);
    const [pagination, setPagination] = useState<MRT_PaginationState>({
        pageIndex: 0,
        pageSize: 20,
    });
    const [saveSuccessful, setSaveSuccessful] = useState<boolean | null>(null);
    const [isAdvancedFiltersOpen, setIsAdvancedFiltersOpen] = useState(false);
    const [activeFilter, setActiveFilter] = useState<CaseFilter | null>(null);
    const [isExportModalOpen, setIsExportModalOpen] = useState(false);
    const [isBulkReassignModalOpen, setIsBulkReassignModalOpen] = useState(false);
    const saveCaseFilter = useSaveCaseFilter();
    const exportCases = useExportCases();

    const [selectedSearchFields, setSelectedSearchFields] = useState<string[]>(
        SEARCH_FIELDS.filter((field) => field.default).map((field) => field.fieldName),
    );

    const { data, isError, isRefetching, isLoading, error, refetch } = useFetchMrtCases({
        columnFilters,
        globalFilter,
        activeFilter,
        pagination,
        sorting,
        searchFields: selectedSearchFields,
    });

    const { data: savedFilters } = useFetchCaseFiltersets({});

    const form = useForm<CasesTableFilter>({
        resolver: zodResolver(casesFilterSchema),
    });

    const caseType = form.watch("caseType");
    const caseStatus = form.watch("caseStatus");
    const daysToSol = form.watch("daysToSol");

    const handleRowClick = (row: MRT_Row<MrtDashboardCase>) => {
        navigate(`/case-detail/${row.original.id}`);
    };

    const handleDeleteRow = (row: MRT_Row<MrtDashboardCase>) => {
        if (window.confirm("Are you sure you want to delete this Case?")) {
            deleteCase.mutate(
                {
                    caseUUID: row.original.idUUID,
                },
                {
                    onSuccess: () => {
                        enqueueSnackbar("Case removed.", { variant: "success" });
                    },
                    onError: (error) => {
                        enqueueSnackbar("Error: Case could not be removed.", { variant: "error" });
                        console.info("error", getErrorMessage(error));
                    },
                },
            );
        }
    };

    const handleAdvancedFilters = () => {
        setIsAdvancedFiltersOpen(true);
    };

    const handleEditFilter = () => {
        setIsAdvancedFiltersOpen(true);
    };

    const handleApplyAdvancedFilters = (filter: CaseFilter) => {
        setActiveFilter(filter);

        if (filter.idUUID) {
            saveCaseFilter.mutate(
                {
                    caseFilter: filter,
                },
                {
                    onSuccess: (data) => {
                        enqueueSnackbar("Filter saved.", { variant: "success" });
                        setActiveFilter(data);
                        setIsAdvancedFiltersOpen(false);
                    },
                    onError: (error) => {
                        enqueueSnackbar("Error: Filter failed to save.", { variant: "error" });
                        console.info("error", getErrorMessage(error));
                    },
                },
            );
        } else {
            // filter is not a saved filter yet
            setIsAdvancedFiltersOpen(false);
        }
    };

    const handleSaveFilter = (name: string, filter: CaseFilterEditing) => {
        const newFilter: CaseFilterEditing = { ...filter, idUUID: null, name: name };

        saveCaseFilter.mutate(
            {
                caseFilter: newFilter,
            },
            {
                onSuccess: (data) => {
                    enqueueSnackbar("Filter saved.", { variant: "success" });
                    setActiveFilter(data);
                    setIsAdvancedFiltersOpen(false);
                    setSaveSuccessful(true);
                },
                onError: (error) => {
                    enqueueSnackbar("Error: Filter failed to save.", { variant: "error" });
                    console.info("error", getErrorMessage(error));
                    setSaveSuccessful(false);
                },
            },
        );
    };

    const handleFilterSelect = (filter: CaseFilter | null) => {
        setActiveFilter(filter);
    };

    const handleClearFilters = () => {
        setActiveFilter(null);
    };

    const handleExport = (fields: AvailableField[]) => {
        exportCases.mutate(
            {
                fieldsInOrder: fields,
                selectedTab: "",
                columnFilters,
                globalFilter,
                activeFilter,
                sorting,
            },
            {
                onSuccess: () => {
                    enqueueSnackbar("Export retrieved.", { variant: "success" });
                    setIsExportModalOpen(false);
                },
                onError: (error) => {
                    enqueueSnackbar("Error: Export failed.", { variant: "error" });
                    console.info("error", getErrorMessage(error));
                },
            },
        );
    };

    const columns = useMemo<MRT_ColumnDef<MrtDashboardCase>[]>(() => {
        const columnDefs: MRT_ColumnDef<MrtDashboardCase>[] = [...caseColumnDefs];

        if (hasPermission("delete:case")) {
            columnDefs.push({
                accessorKey: "delete",
                header: "Delete",
                size: 50,
                Cell: ({ row }) => (
                    <IconButton
                        onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                            event.stopPropagation();
                            handleDeleteRow(row);
                        }}
                    >
                        <DeleteIcon />
                    </IconButton>
                ),
            });
        }

        return columnDefs;
    }, [hasPermission, handleDeleteRow]);

    const table = useMaterialReactTable({
        columns,
        data: isLoading || isRefetching || isError || data === undefined ? [] : data.data,
        enableColumnActions: false,
        defaultColumn: {
            size: 30,
            muiTableBodyCellProps: {
                sx: {
                    wordWrap: "break-word",
                    whiteSpace: "normal",
                },
            },
        },
        initialState: {
            showColumnFilters: false,
            showGlobalFilter: true,
            density: "compact",
        },

        // top toolbar
        enableTopToolbar: true,
        enableDensityToggle: false,
        enableFullScreenToggle: false,
        enableHiding: false,
        enableColumnFilters: false,
        enableGlobalFilter: false,

        renderTopToolbarCustomActions: () => (
            <SearchableToolbar
                searchFields={SEARCH_FIELDS}
                globalFilter={globalFilter}
                onGlobalFilterChange={setGlobalFilter}
                selectedSearchFields={selectedSearchFields}
                onSelectedSearchFieldsChange={setSelectedSearchFields}
                placeholder="Search cases..."
            />
        ),

        manualFiltering: true,
        manualPagination: true,
        manualSorting: true,
        muiToolbarAlertBannerProps: isError
            ? {
                  color: "error",
                  children: "Error loading data",
              }
            : undefined,
        onColumnFiltersChange: setColumnFilters,
        onGlobalFilterChange: setGlobalFilter,
        onPaginationChange: setPagination,
        onSortingChange: setSorting,
        rowCount: data?.meta.totalRowCount ?? 0,
        state: {
            columnFilters,
            globalFilter,
            isLoading,
            pagination,
            showAlertBanner: isError,
            showProgressBars: isRefetching,
            showLoadingOverlay: isLoading || isRefetching,
            sorting,
        },
        muiTableBodyRowProps: ({ row }: { row: MRT_Row<MrtDashboardCase> }) => {
            return {
                onClick: () => handleRowClick(row),
                sx: {
                    cursor: "pointer",
                    "& .MuiTableCell-root": {
                        ...getSolReportMrtStyle({
                            caseStatus: row.original.caseStatus ?? "",
                            dateOfBirth: row.original.idPeople?.dateofbirth ?? null,
                        }),
                    },
                },
            };
        },
        renderToolbarInternalActions: () => (
            <Box sx={{ display: "flex", alignItems: "center", gap: "1rem" }}>
                <SearchBar
                    onAdvancedFilters={handleAdvancedFilters}
                    savedFilters={savedFilters?.data ?? []}
                    onFilterSelect={handleFilterSelect}
                    selectedFilter={activeFilter}
                >
                    {activeFilter && (
                        <FilterBadge
                            filterName={activeFilter?.name}
                            onClear={handleClearFilters}
                            onEdit={handleEditFilter}
                            onExport={() => setIsExportModalOpen(true)}
                            onBulkReassign={() => setIsBulkReassignModalOpen(true)}
                        />
                    )}
                </SearchBar>
            </Box>
        ),
    });

    useEffect(() => {
        void refetch();
    }, [globalFilter, activeFilter]);

    useEffect(() => {
        table.getColumn("caseType")?.setFilterValue(caseType);
        table.getColumn("caseStatus")?.setFilterValue(caseStatus);
        table.getColumn("solDays")?.setFilterValue(daysToSol);
    }, [caseType, caseStatus, daysToSol, table.getColumn]);

    if (isError) {
        return <div>{error.message}</div>;
    }

    return (
        <>
            <Paper elevation={0}>
                <Typography variant={"h5"}>Filter</Typography>
                <Stack
                    sx={{
                        flexDirection: "row",
                        justifyContent: "space-between",
                        gap: 1,
                        py: 2,
                    }}
                >
                    <CaseTypesForCasesTable form={form} />
                    <CaseStatusesForCasesTable form={form} />
                    <DaysToSolForCasesTable form={form} />
                </Stack>
            </Paper>

            <MaterialReactTable table={table} />

            <AdvancedFiltersModal
                open={isAdvancedFiltersOpen}
                onClose={() => {
                    setIsAdvancedFiltersOpen(false);
                }}
                onApply={handleApplyAdvancedFilters}
                onSaveFilter={handleSaveFilter}
                editingFilter={activeFilter}
                saveSuccessful={saveSuccessful}
                setSaveSuccessful={setSaveSuccessful}
            />

            <ExportModal
                open={isExportModalOpen}
                onClose={() => setIsExportModalOpen(false)}
                onExport={handleExport}
                activeFilter={activeFilter}
            />

            {isBulkReassignModalOpen && (
                <BulkReassignmentModal
                    open={isBulkReassignModalOpen}
                    onClose={() => setIsBulkReassignModalOpen(false)}
                    activeFilter={activeFilter}
                    columnFilters={columnFilters}
                    globalFilter={globalFilter}
                    pagination={pagination}
                    sorting={sorting}
                />
            )}
        </>
    );
};

export default MrtCaseTable;
